// src/api/adminReferralsApi.ts
// Admin › Referidos (super_admin) contra lealify-base /api/admin/referrals (F1, 12-oct-2026).
// Programa de referidos de PLATAFORMA: quién trajo a qué negocio que le paga a Lealify y qué gana.
// Plan: forobeta-ops/docs/lealify-referidos-plataforma-plan-20261012.md
import { baseApi } from './baseClient';

export type ReferralKind = 'first_year_pct' | 'recurring_pct' | 'free_months';

export interface ReferralScheme {
  id: number; referrer_id: number; kind: ReferralKind; rate: string | null;
  months_cap: number | null; free_months: number | null; effective_from: string; notes: string | null;
}
export interface ReferralTotals {
  currency: string; earned: number; paid: number; pending: number;
  months_applied: number; months_pending: number; favor: number;
}
export interface Referrer {
  id: number; name: string; phone: string | null; email: string | null; code: string;
  own_tenant_id: number | null; own_tenant_name: string | null; active: number; notes: string | null;
  referrals_count?: number; current_scheme?: ReferralScheme | null; totals?: ReferralTotals[];
  /** F3: visitantes únicos del enlace lealify.com/r/<code> y altas web que llegaron por él. */
  link_visitors?: number; link_signups?: number;
}
export interface ReferralRow {
  id: number; referred_tenant_id: number; tenant_name: string; tenant_slug: string; kind: ReferralKind;
  rate: string | null; months_cap: number | null; free_months: number | null; attributed_at: string;
  first_paid_at: string | null; status: 'active' | 'ended' | 'void'; notes: string | null;
  sub_status: string | null; is_trial: number | null; expires_at: string | null; earned: string; months: string;
}
export interface EarningRow {
  id: number; referral_id: number; payment_id: number | null; period: string; base_amount: string; currency: string;
  rate: string | null; amount: string; paid_amount: string; months_granted: number | null;
  status: 'pending' | 'paid' | 'applied' | 'void'; description: string | null; tenant_name: string; created_at: string;
}
export interface PayoutRow {
  id: number; amount: string; currency: string; method: string | null; paid_at: string;
  unallocated: string; notes: string | null;
}
export interface MonthlyRow { period: string; currency: string; n: number; amount: string; months: string }
export interface ReferrerDetail {
  referrer: Referrer; schemes: ReferralScheme[]; referrals: ReferralRow[];
  earnings: EarningRow[]; payouts: PayoutRow[]; monthly: MonthlyRow[];
}
export interface TenantOption { id: number; name: string; slug: string; vertical: string | null; referral_id: number | null; referrer_name: string | null }
export interface SchemeInput { kind: ReferralKind; rate?: number | null; months_cap?: number | null; free_months?: number | null; effective_from?: string }

export const adminReferralsApi = {
  summary: async () => (await baseApi.get<{ referrers: Referrer[] }>('/admin/referrals/summary')).referrers,
  tenants: async () => (await baseApi.get<{ tenants: TenantOption[] }>('/admin/referrals/tenants')).tenants,
  detail: (id: number) => baseApi.get<ReferrerDetail>(`/admin/referrals/referrers/${id}`),
  createReferrer: (b: { name: string; code: string; phone?: string; email?: string; own_tenant_id?: number | null; notes?: string; scheme: SchemeInput; effective_from?: string }) =>
    baseApi.post<{ ok: true; id: number }>('/admin/referrals/referrers', b),
  updateReferrer: (id: number, b: Partial<Pick<Referrer, 'name' | 'phone' | 'email' | 'notes'>> & { own_tenant_id?: number | null; active?: boolean }) =>
    baseApi.put<{ ok: true }>(`/admin/referrals/referrers/${id}`, b),
  addScheme: (id: number, s: SchemeInput) => baseApi.post<{ ok: true; id: number }>(`/admin/referrals/referrers/${id}/schemes`, s),
  attribute: (b: { referrer_id: number; tenant_id: number; attributed_at?: string; notes?: string }) =>
    baseApi.post<{ ok: true; id: number }>('/admin/referrals/attributions', b),
  unattribute: (referralId: number) => baseApi.del<{ ok: true }>(`/admin/referrals/attributions/${referralId}`),
  manualEarning: (b: { referral_id: number; base_amount?: number; rate?: number | null; amount?: number | null; currency?: string;
    months_granted?: number | null; apply_months?: boolean; description?: string; date?: string }) =>
    baseApi.post<{ ok: true; id: number }>('/admin/referrals/earnings', b),
  voidEarning: (id: number, reason: string) => baseApi.post<{ ok: true }>(`/admin/referrals/earnings/${id}/void`, { reason }),
  payout: (b: { referrer_id: number; amount: number; currency?: string; method?: string; paid_at?: string; notes?: string }) =>
    baseApi.post<{ ok: true; payoutId: number; unallocated: number }>('/admin/referrals/payouts', b),
};

export const KIND_LABEL: Record<ReferralKind, string> = {
  first_year_pct: '% el primer año', recurring_pct: '% recurrente', free_months: 'Meses gratis',
};
export function schemeText(s: { kind: ReferralKind; rate: string | number | null; months_cap?: number | null; free_months: number | null } | null | undefined): string {
  if (!s) return 'Sin esquema';
  if (s.kind === 'free_months') return `${s.free_months} meses gratis por referido que paga`;
  const r = Number(s.rate);
  if (s.kind === 'first_year_pct') return `${r} % de cada pago el primer año`;
  return s.months_cap ? `${r} % de cada pago durante ${s.months_cap} meses` : `${r} % de cada pago, para siempre`;
}
